import type { SiteStoreLocation } from "@lib/data/site-manager"
import type { HttpTypes } from "@medusajs/types"
import type { SizeChart } from "@modules/size-chart/lib/fetch-size-chart"

type ProductOptionLike = Pick<HttpTypes.StoreProductOption, "id"> | null | undefined
type OptionMap = Record<string, string | undefined>

type ProductStoryNoteSources = {
  storyItems?: unknown
  storyPoints: string[]
  details?: string[]
  materials?: string[]
  care?: string[]
}

export type ProductStoryItem = {
  heading: string
  body: string
}

export type InlineSizeMeasurement = {
  size: string
  values: {
    label: string
    value: string
    unit: string
  }[]
}

export type ProductStoreStockRow = {
  id: string
  code: string
  name: string
  type?: string
  address?: string
  phone?: string
  hours?: string
  status: "in" | "low" | "out"
  label: string
  detail: string
  qty: number | null
  stockKnown: boolean
}

const ODOO_LOCATION_CODES: Record<string, string> = {
  banani: "BNNW",
  "bashundhara-city": "BSHCW",
  "jamuna-future-park": "JFPW",
  mirpur: "MRPW",
  wari: "WRBR",
  "ecommerce-store": "CTWH",
}

const normalize = (value?: string | null) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")

const stockNumber = (value: unknown) => {
  const qty = Number(value)
  return Number.isFinite(qty) ? Math.max(0, Math.trunc(qty)) : null
}

const getShowroomStock = (variant?: HttpTypes.StoreProductVariant | null) => {
  const metadata = (variant?.metadata || {}) as Record<string, unknown>
  const showroomStock = metadata.showroom_stock

  if (!showroomStock || typeof showroomStock !== "object" || Array.isArray(showroomStock)) {
    return {}
  }

  return showroomStock as Record<string, unknown>
}

const hasShowroomStock = (variant?: HttpTypes.StoreProductVariant | null) =>
  Object.keys(getShowroomStock(variant)).length > 0

const locationCode = (location: SiteStoreLocation) => {
  const id = String(location.id || "").toLowerCase()
  if (ODOO_LOCATION_CODES[id]) {
    return ODOO_LOCATION_CODES[id]
  }

  const name = String(location.name || "").toLowerCase()
  if (name.includes("banani")) return "BNNW"
  if (name.includes("bashundhara")) return "BSHCW"
  if (name.includes("jamuna")) return "JFPW"
  if (name.includes("mirpur")) return "MRPW"
  if (name.includes("wari")) return "WRBR"
  if (name.includes("ecommerce") || name.includes("warehouse")) return "CTWH"
  return normalize(location.id || location.name)
}

const statusForQty = (qty: number): ProductStoreStockRow["status"] => {
  if (qty <= 0) return "out"
  if (qty <= 2) return "low"
  return "in"
}

const labelForStatus = (status: ProductStoreStockRow["status"]) => {
  if (status === "out") return "Out of stock"
  if (status === "low") return "Low stock"
  return "In stock"
}

export const getSelectedColorLabel = (
  colorOption: ProductOptionLike,
  options: OptionMap
) => {
  if (!colorOption?.id) {
    return null
  }

  return options[colorOption.id] || null
}

export const getSizeChartMeasurement = (
  chart: SizeChart | null | undefined,
  selectedSize?: string | null
): InlineSizeMeasurement | null => {
  if (!chart?.rows?.length || !chart.columns?.length || !selectedSize) {
    return null
  }

  const labelColumn =
    chart.columns.find((column) => column.type === "label") || chart.columns[0]
  const row = chart.rows.find(
    (candidate) => normalize(String(candidate[labelColumn.key] ?? "")) === normalize(selectedSize)
  )

  if (!row) {
    return null
  }

  const values = chart.columns
    .filter((column) => column.type === "measurement")
    .map((column) => ({
      label: column.label,
      value: String(row[column.key] ?? "-"),
      unit: chart.unit || "inch",
    }))
    .filter((measurement) => measurement.value !== "")

  return {
    size: String(row[labelColumn.key] ?? selectedSize),
    values,
  }
}

export const buildProductStoryNotes = ({
  storyPoints,
}: ProductStoryNoteSources): string[] => {
  return storyPoints.map((note) => note.trim()).filter(Boolean)
}

const STORY_FALLBACK_HEADINGS = ["Design", "Fabric", "Care"]

const trimText = (value: unknown) => String(value || "").trim()

const sentenceJoin = (items?: string[]) =>
  (items || [])
    .map((item) => item.trim().replace(/[.。]+$/g, ""))
    .filter(Boolean)
    .slice(0, 3)
    .map((item) => `${item}.`)
    .join(" ")

const parseStoryItems = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  return []
}

export const buildProductStoryItems = ({
  storyItems,
  storyPoints,
  details,
  materials,
  care,
}: ProductStoryNoteSources): ProductStoryItem[] => {
  const structured = parseStoryItems(storyItems)
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null
      }

      const source = item as Record<string, unknown>
      const heading = trimText(source.heading || source.title || source.label)
      const body = trimText(source.body || source.copy || source.text || source.description)

      return heading && body ? { heading, body } : null
    })
    .filter((item): item is ProductStoryItem => Boolean(item))
    .slice(0, 3)

  if (structured.length) {
    return structured
  }

  const explicitPoints = storyPoints.map((note) => note.trim()).filter(Boolean)
  if (explicitPoints.length) {
    return explicitPoints.slice(0, 3).map((body, index) => ({
      heading: STORY_FALLBACK_HEADINGS[index] || "Story",
      body,
    }))
  }

  return [
    { heading: "Design", body: sentenceJoin(details) },
    { heading: "Fabric", body: sentenceJoin(materials) },
    { heading: "Care", body: sentenceJoin(care) },
  ].filter((item) => item.body)
}

export const buildStoreStockRows = ({
  locations,
  selectedVariant,
  variants,
}: {
  locations: SiteStoreLocation[]
  selectedVariant?: HttpTypes.StoreProductVariant | null
  variants?: HttpTypes.StoreProductVariant[]
}): ProductStoreStockRow[] => {
  const retailLocations = locations
    .filter((location) => location.enabled !== false)
    .filter((location) => {
      const type = String(location.type || "retail").toLowerCase()
      return type === "retail" || type === "ecommerce"
    })

  const productHasOdooStock = (variants || []).some(hasShowroomStock)
  const selectedStock = getShowroomStock(selectedVariant)

  return retailLocations.map((location) => {
    const code = locationCode(location)
    const qty = stockNumber(selectedStock[code])

    if (!productHasOdooStock) {
      return {
        id: location.id || code,
        code,
        name: location.name,
        type: location.type,
        address: location.address,
        phone: location.phone,
        hours: location.hours,
        status: "in",
        label: "In stock",
        detail: "Available in showroom",
        qty: null,
        stockKnown: false,
      }
    }

    const knownQty = qty ?? 0
    const status = statusForQty(knownQty)

    return {
      id: location.id || code,
      code,
      name: location.name,
      type: location.type,
      address: location.address,
      phone: location.phone,
      hours: location.hours,
      status,
      label: labelForStatus(status),
      detail: status === "out" ? "Selected size unavailable" : knownQty < 5 ? `${knownQty} available` : "In stock",
      qty: knownQty,
      stockKnown: true,
    }
  })
}
